import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HomeIcon from '@mui/icons-material/Home';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const steps = [
  {
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 40 }} />,
    title: "APPLY ONLINE",
    description: "Start your journey with our simple online application process"
  },
  {
    icon: <HomeIcon sx={{ fontSize: 40 }} />,
    title: "FIND YOUR HOME",
    description: "Browse through our extensive collection of properties"
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
    title: "GET APPROVED",
    description: "Get your mortgage approved with our expert guidance"
  }
];

const viewportConfig = { 
  threshold: 0.1,
  triggerOnce: false,
  rootMargin: "-50px 0px"
};

const containerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
      staggerChildren: 0.15,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const ProcessSteps = () => {
  const [ref, inView] = useInView(viewportConfig);
  const controls = useAnimation();

  React.useEffect(() => {
    controls.start(inView ? "visible" : "hidden");
  }, [controls, inView]);

  return (
    <Box
      ref={ref}
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
      sx={{ 
        width: '100%',
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 }
      }}
    >
      <Box sx={{ 
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          position: 'relative'
        }}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Box
                component={motion.div}
                variants={itemVariants}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  position: 'relative',
                  flex: 1
                }}
              >
                <Box sx={{ 
                  color: '#64B5F6',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(100, 181, 246, 0.1)'
                }}>
                  {step.icon}
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: '#1A1A1A',
                  mb: 1
                }}>
                  {step.title}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  maxWidth: '250px'
                }}>
                  {step.description}
                </Typography>
              </Box>
              {index < steps.length - 1 && (
                <Box
                  component={motion.div}
                  variants={itemVariants}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#64B5F6',
                    color: 'white',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(100, 181, 246, 0.2)',
                      zIndex: -1,
                      animation: 'pulse 2s infinite'
                    }
                  }}
                >
                  <ArrowForwardIcon />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProcessSteps; 